import React, {useEffect, useState, useCallback} from "react"
import {Box, Typography, Avatar, Skeleton, InputBase, IconButton, Stack, Collapse} from "@mui/material"
import {InsightsRounded, SearchRounded, RefreshRounded, ChevronLeftRounded, ChevronRightRounded, ExpandMoreRounded} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import {useData} from "@/context/PackagingDataProvider"
import {useUser} from "@/context/UserProvider"
import {PageHeader, Badge} from "@/components/Layout"
import {packagingTokens as tk} from "@/theme/packagingTokens"
import {countryCodeToName, languageCodeToName} from "@/utils"
import {
    actionTone, formatAction, formatEntityType, relativeTime, initialsFor, avatarColor, formatDateTime,
    isFilterPresetsLog, filterPresetsChange, diffFilterPresets, filterPresetsScopeLabel, PresetDetail,
} from "./activityUtils"

const PER_PAGE = 12

// Packaging-scoped Activity Logs (audit events filtered to source_app=packaging),
// rendered as the blueprint's avatar "activity feed".
export default function ActivityView() {
    const {t, i18n} = useTranslation(["common"])
    const {getPackagingActivityLogs, commonFilters, ranges} = useData()
    const {organisation} = useUser()
    const orgFilter = commonFilters?.organisation ?? ""

    const [logs, setLogs] = useState<any[] | null>(null)
    const [total, setTotal] = useState<number>(0)
    const [page, setPage] = useState<number>(1)
    const [search, setSearch] = useState<string>("")
    const [debounced, setDebounced] = useState<string>("")
    // Rows whose filter detail is expanded, keyed by feed index. Reset on every
    // fetch — indices refer to the current page only.
    const [expanded, setExpanded] = useState<Record<number, boolean>>({})

    useEffect(() => {
        const h = setTimeout(() => setDebounced(search), 400)
        return () => clearTimeout(h)
    }, [search])

    const fetch = useCallback(() => {
        if (!organisation) return
        setLogs(null)
        setExpanded({})
        getPackagingActivityLogs(page, PER_PAGE, debounced, orgFilter || undefined).then((res: any) => {
            setLogs(res?.events ?? [])
            setTotal(res?.total ?? 0)
        })
    }, [organisation, page, debounced, orgFilter, getPackagingActivityLogs])

    useEffect(() => { fetch() }, [fetch])
    useEffect(() => { setPage(1) }, [debounced, orgFilter])

    const hasMore = page * PER_PAGE < total
    const showingFrom = total === 0 ? 0 : (page - 1) * PER_PAGE + 1
    const showingTo = Math.min(page * PER_PAGE, total)

    // One preset entry as human text — resolved names when the codes are
    // known, the raw audit segment otherwise so nothing is silently dropped.
    const presetLabel = (p: PresetDetail): string => {
        const parts: string[] = []
        if (p.country) parts.push(`${countryCodeToName(p.country) ?? p.country} (${p.country})`)
        if (p.language) parts.push(`${languageCodeToName(p.language) ?? p.language} (${p.language})`)
        return parts.length > 0 ? parts.join(" · ") : p.raw
    }

    // One column of the diff: a titled list, or nothing when empty — the
    // panel only shows what actually happened.
    const renderPresetList = (title: string, details: PresetDetail[], color: string, marker: string) => {
        if (details.length === 0) return null
        return (
            <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color, mb: 0.5}}>
                    {title} ({details.length})
                </Typography>
                {details.map((p, idx) => (
                    <Typography key={idx} sx={{fontSize: "12.5px", color: tk.color.textSecondary, lineHeight: 1.6}}>
                        <Box component={"span"} sx={{color, fontWeight: 700, mr: 0.5}}>{marker}</Box>
                        {presetLabel(p)}
                    </Typography>
                ))}
            </Box>
        )
    }

    // Collapsible detail for a filters-propagation event: which filters were
    // added, removed and kept by this change.
    const renderFilterDetails = (log: any, i: number) => {
        const change = filterPresetsChange(log)
        if (!change) return null
        const isOpen = !!expanded[i]
        const diff = diffFilterPresets(change.old_value, change.new_value)
        const nothingChanged = diff.added.length === 0 && diff.removed.length === 0
        return (
            <>
                <Box
                    component={"button"}
                    onClick={() => setExpanded((e) => ({...e, [i]: !e[i]}))}
                    aria-expanded={isOpen}
                    sx={{
                        display: "inline-flex", alignItems: "center", gap: 0.25, mt: 0.75, p: 0,
                        border: "none", background: "none", cursor: "pointer",
                        fontSize: "12px", fontWeight: 600, color: tk.color.greenDark, fontFamily: "inherit",
                    }}
                >
                    {isOpen ? t("activity_hide_details") : t("activity_show_details")}
                    <ExpandMoreRounded sx={{fontSize: 16, transition: "transform .15s", transform: isOpen ? "rotate(180deg)" : "none"}} />
                </Box>
                <Collapse in={isOpen} unmountOnExit>
                    <Box sx={{
                        display: "flex", gap: 3, mt: 1, px: 1.75, py: 1.5,
                        border: `1px solid ${tk.color.borderLight}`, borderRadius: tk.radius.sm,
                        backgroundColor: tk.color.green50,
                    }}>
                        {renderPresetList(t("activity_filters_added"), diff.added, tk.color.greenDark, "+")}
                        {renderPresetList(t("activity_filters_removed"), diff.removed, tk.color.red, "−")}
                        {renderPresetList(t("activity_filters_unchanged"), diff.unchanged, tk.color.textTertiary, "=")}
                        {nothingChanged && diff.unchanged.length === 0 && (
                            <Typography sx={{fontSize: "12.5px", color: tk.color.textTertiary, fontStyle: "italic"}}>
                                {t("activity_filters_none")}
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </>
        )
    }

    // Filters-propagation events headline the scope and the date — "Brand ·
    // 2 Oct 2026, 14:32", "Brand › Range · …" — with the author underneath,
    // so a reader scanning for a brand finds its changes without reading
    // every sentence.
    const renderFilterPresetsItem = (log: any, i: number) => (
        <Box sx={{flex: 1, minWidth: 0}}>
            <Typography sx={{fontSize: "13.5px", lineHeight: 1.45, color: tk.color.textPrimary}}>
                <Box component={"strong"} sx={{fontWeight: 700, color: tk.color.greenDark}}>
                    {filterPresetsScopeLabel(log, ranges) || formatEntityType(log.entity_type)}
                </Box>
                <Box component={"span"} sx={{color: tk.color.textTertiary}}> · </Box>
                {formatDateTime(log["@timestamp"], i18n?.language)}
            </Typography>
            <Typography sx={{fontSize: "12.5px", color: tk.color.textSecondary, mt: 0.25}}>
                {t("activity_filters_updated_by", {user: log.user_email || log.user_uuid || "—"})}
            </Typography>
            <Stack direction={"row"} spacing={1.5} alignItems={"center"} sx={{mt: 0.625}}>
                <Typography sx={{fontSize: "12px", color: tk.color.textTertiary}}>{relativeTime(log["@timestamp"])}</Typography>
                <Badge tone={actionTone(log.action)}>{formatAction(log.action)}</Badge>
                {(log.entity_type ?? "").toLowerCase() === "packaging_range" && (
                    <Badge tone={"gray"}>{t("range")}</Badge>
                )}
            </Stack>
            {renderFilterDetails(log, i)}
        </Box>
    )

    const renderItem = (log: any, i: number, last: boolean) => (
        <Box key={i} sx={{
            display: "flex", gap: 1.75, px: 2.75, py: 2, alignItems: "flex-start",
            borderBottom: last ? "none" : `1px solid ${tk.color.borderLight}`,
            transition: "background .1s",
            "&:hover": {backgroundColor: tk.color.green50},
        }}>
            <Avatar sx={{width: 34, height: 34, fontSize: "12px", fontWeight: 700, bgcolor: avatarColor(log.user_email || log.user_uuid)}}>
                {initialsFor(log.user_email || log.user_uuid)}
            </Avatar>
            {isFilterPresetsLog(log) ? renderFilterPresetsItem(log, i) : (
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontSize: "13.5px", lineHeight: 1.45, color: tk.color.textPrimary}}>
                        <Box component={"strong"} sx={{fontWeight: 600}}>{log.user_email || log.user_uuid || "—"}</Box>
                        {" "}{formatAction(log.action).toLowerCase()}{" "}
                        {formatEntityType(log.entity_type).toLowerCase()}
                        {log.entity_label && (
                            <Box component={"span"} sx={{color: tk.color.greenDark, fontWeight: 600}}> {log.entity_label}</Box>
                        )}
                    </Typography>
                    <Stack direction={"row"} spacing={1.5} alignItems={"center"} sx={{mt: 0.625}}>
                        <Typography sx={{fontSize: "12px", color: tk.color.textTertiary}}>{relativeTime(log["@timestamp"])}</Typography>
                        <Badge tone={actionTone(log.action)}>{formatAction(log.action)}</Badge>
                    </Stack>
                </Box>
            )}
        </Box>
    )

    return (
        <Box>
            <PageHeader
                title={t("nav_team_activity")}
                subtitle={t("activity_subtitle")}
                actions={
                    <IconButton onClick={fetch} sx={{border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.sm}}>
                        <RefreshRounded sx={{fontSize: 18, color: tk.color.textSecondary}} />
                    </IconButton>
                }
            />

            {/* Search */}
            <Box sx={{display: "flex", alignItems: "center", gap: 1, maxWidth: 320, mb: 2.5, px: 1.5, py: 0.75,
                border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.sm, backgroundColor: tk.color.surface,
                "&:focus-within": {borderColor: tk.color.borderStrong}}}>
                <SearchRounded sx={{fontSize: 18, color: tk.color.textTertiary}} />
                <InputBase fullWidth value={search} onChange={(e) => setSearch(e.target.value)}
                           placeholder={t("activity_search")} sx={{fontSize: "13px"}} />
            </Box>

            {/* Feed */}
            <Box sx={{backgroundColor: tk.color.surface, border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.card, overflow: "hidden"}}>
                {logs === null ? (
                    Array.from({length: 5}).map((_, i) => (
                        <Box key={i} sx={{display: "flex", gap: 1.75, px: 2.75, py: 2, borderBottom: i < 4 ? `1px solid ${tk.color.borderLight}` : "none"}}>
                            <Skeleton variant={"circular"} width={34} height={34} />
                            <Box sx={{flex: 1}}>
                                <Skeleton variant={"text"} width={"70%"} />
                                <Skeleton variant={"text"} width={"30%"} />
                            </Box>
                        </Box>
                    ))
                ) : logs.length === 0 ? (
                    <Box sx={{py: 8, textAlign: "center"}}>
                        <InsightsRounded sx={{fontSize: 32, color: tk.color.textTertiary, mb: 1}} />
                        <Typography sx={{fontSize: "15px", fontWeight: 700, mb: 0.5}}>{t("activity_empty_title")}</Typography>
                        <Typography sx={{fontSize: "13px", color: tk.color.textSecondary, maxWidth: 420, mx: "auto"}}>{t("activity_empty_body")}</Typography>
                    </Box>
                ) : (
                    logs.map((log: any, i: number) => renderItem(log, i, i === logs.length - 1))
                )}

                {logs !== null && logs.length > 0 && (
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.75, py: 1.5, borderTop: `1px solid ${tk.color.borderLight}`}}>
                        <Typography sx={{fontSize: "12.5px", color: tk.color.textTertiary}}>
                            {t("activity_showing", {from: showingFrom, to: showingTo, total})}
                        </Typography>
                        <Stack direction={"row"} spacing={0.5}>
                            <IconButton size={"small"} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                <ChevronLeftRounded fontSize={"small"} />
                            </IconButton>
                            <IconButton size={"small"} disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
                                <ChevronRightRounded fontSize={"small"} />
                            </IconButton>
                        </Stack>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
